import { type EntitlementSet, PLAN_DEFAULTS, type Plan } from '@parcel/types';
import { ApiError, apiFetch } from './api.js';
import { isSupabaseConfigured } from './supabase.js';

export interface WorkspaceSettings {
  fiscalYearStart: string;
}

export interface WorkspaceCapabilities {
  canUpdateSettings: boolean;
  canManageMembers: boolean;
  canManageInvites: boolean;
  canUploadAssets: boolean;
  canManageApiKeys: boolean;
  canManageSpecTools: boolean;
  canManageConnections: boolean;
  canManageSkills: boolean;
  canUseDeveloperMcp: boolean;
}

export interface WorkspaceSummary {
  id: string;
  name: string;
  slug: string;
  role: 'owner' | 'admin' | 'member';
  companyId: string;
  companyName: string;
  companyDomain: string | null;
  companyDomains: string[];
  logoUrl: string | null;
  onboardingComplete: boolean;
  settings: WorkspaceSettings;
  capabilities: WorkspaceCapabilities;
  // OPTIONAL: present on the single-workspace bootstrap (GET/PATCH/POST), absent on GET /workspaces LIST rows.
  effectivePlan?: Plan;
  entitlements?: EntitlementSet;
}

type WorkspaceDestinationInput = Pick<WorkspaceSummary, 'slug' | 'onboardingComplete'>;
type CreateWorkspaceInput = {
  name: string;
  slug: string;
  domainAccess: boolean;
  domains: string[];
};
type UpdateWorkspaceInput = {
  name?: string;
  settings?: Partial<WorkspaceSettings>;
};
export interface WorkspaceRedirectLocation {
  href: string;
  pathname: string;
  search: string;
}
type WorkspaceDomainInput = {
  domain: string;
  verificationEmail?: string;
};

export interface OnboardingStatus {
  profileCompleted: boolean;
  invitesCompleted: boolean;
  completedAt: string | null;
}

export interface UserProfile {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  title: string | null;
  avatarUrl: string | null;
}

export type UserProfileNameSource = Pick<UserProfile, 'displayName' | 'firstName'>;

export type UserProfileUpdateInput = {
  firstName: string;
  lastName: string;
  displayName?: string | null;
  title?: string | null;
};

export type UserProfileUpdatePayload = {
  firstName: string;
  lastName: string;
  displayName?: string | null;
  title?: string | null;
};

export interface WorkspaceMembersResponse {
  canManageMembers: boolean;
  seats: { used: number; limit: number; isOver: boolean; quantity: number; canManage: boolean };
  members: Array<{
    id: string;
    type: 'member';
    name: string;
    email: string;
    role: 'owner' | 'admin' | 'member';
    status: 'Active';
    lastActive: string;
    avatarUrl: string | null;
  }>;
  invitations: Array<{
    id: string;
    type: 'invitation';
    name: string;
    email: string;
    role: 'admin' | 'member';
    status: 'Pending' | 'Expired' | 'Revoked';
    deliveryStatus: 'sent' | 'not_sent';
    lastActive: string;
    expiresAt: string;
    sentAt: string | null;
  }>;
}

export interface InviteWorkspaceMembersResponse {
  invitations: Array<{ id: string; email: string }>;
  invalid: string[];
  errors?: Array<{
    email?: string;
    code: string;
    message: string;
  }>;
}

export function getEmailHandle(email: string | null | undefined): string {
  const trimmed = email?.trim();
  if (!trimmed) return 'Signed-in user';
  return trimmed.split('@')[0] || 'Signed-in user';
}

export function getUserProfileDisplayLabel(input: {
  displayName?: string | null;
  firstName?: string | null;
  email?: string | null;
}): string {
  const displayName = input.displayName?.trim();
  if (displayName) return displayName;
  const firstName = input.firstName?.trim();
  if (firstName) return firstName;
  return getEmailHandle(input.email);
}

function getAuthMetadataString(
  metadata: Record<string, unknown>,
  keys: readonly string[],
): string | null {
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === 'string') return value;
  }
  return null;
}

export function getStructuredAuthMetadataProfileFallback(
  metadata: Record<string, unknown> | null | undefined,
): UserProfileNameSource | null {
  if (!isRecord(metadata)) return null;

  const displayName = getAuthMetadataString(metadata, ['displayName', 'display_name', 'nickname']);
  const firstName = getAuthMetadataString(metadata, ['firstName', 'first_name']);

  if (displayName === null && firstName === null) return null;

  return { displayName, firstName };
}

export function getUserProfileNameSource(
  profile: UserProfileNameSource | null | undefined,
  transientFallback: UserProfileNameSource | null | undefined,
): UserProfileNameSource | null {
  return profile ?? transientFallback ?? null;
}

function normalizeOptionalProfileString(value: string | null | undefined): string | null {
  return value?.trim() || null;
}

export function normalizeUserProfileUpdateInput(
  input: UserProfileUpdateInput,
): UserProfileUpdatePayload {
  const firstName = input.firstName.trim();
  const lastName = input.lastName.trim();
  if (!firstName || !lastName) {
    throw new ApiError(400, {
      error: {
        code: 'invalid_profile_name',
        message: 'First name and last name are required.',
      },
    });
  }

  const hasDisplayName = input.displayName !== undefined;
  const hasTitle = input.title !== undefined;
  const displayName = normalizeOptionalProfileString(input.displayName);
  const title = normalizeOptionalProfileString(input.title);

  return {
    firstName,
    lastName,
    ...(hasDisplayName ? { displayName } : {}),
    ...(hasTitle ? { title } : {}),
  };
}

export function buildUserProfileUpdateRequest(input: UserProfileUpdateInput): RequestInit {
  return {
    method: 'PATCH',
    body: JSON.stringify(normalizeUserProfileUpdateInput(input)),
  };
}

const workspaceScopedSections = new Set([
  'feed',
  // Kept so an old /<slug>/notifications link still resolves to a section and redirects.
  'notifications',
  // Kept so an old /<slug>/home link still resolves to a section and redirects.
  'home',
  'projects',
  'accounts',
  'contacts',
  'settings',
  'welcome',
  'connectors',
  'knowledge',
  'skills',
  'spec',
]);

const reservedTopLevelSections = new Set([
  '',
  'signup',
  'signin',
  'login',
  'auth',
  'api',
  'settings',
  'feed',
  'notifications',
  'home',
  'projects',
  'accounts',
  'contacts',
  'connectors',
  'knowledge',
  'skills',
  'alerts',
  'workspaces',
  'workspace',
  'onboarding',
  'docs',
  'pricing',
  'privacy',
  'terms',
  'health',
]);

const legacyWorkspaceSections = new Set([
  'feed',
  // Old bookmarks and already-sent alert emails still point at /notifications and /home.
  'notifications',
  'home',
  'projects',
  'accounts',
  'contacts',
  'settings',
  // A dossier's "Open in Parcel" lands on /spec?ask=...; the workspace is only known after sign-in.
  'spec',
]);
const localSecurityDomains = new Map<string, string[]>();
const inviteEmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getLocalSecurityDomains(workspaceSlug: string): string[] {
  const domains = localSecurityDomains.get(workspaceSlug) ?? ['parcelengineering.com'];
  localSecurityDomains.set(workspaceSlug, domains);
  return domains;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function getErrorMessage(error: unknown): string {
  return isRecord(error) && typeof error.message === 'string' ? error.message : 'Request failed.';
}

function getErrorCode(error: unknown): string {
  return isRecord(error) && typeof error.code === 'string' ? error.code : 'api_error';
}

export function getInviteErrorResponse(body: unknown): InviteWorkspaceMembersResponse | null {
  if (!isRecord(body)) return null;

  const invitations = Array.isArray(body.invitations)
    ? body.invitations
        .filter(
          (item): item is { id: string; email: string } =>
            isRecord(item) && typeof item.id === 'string' && typeof item.email === 'string',
        )
        .map((item) => ({ id: item.id, email: item.email }))
    : [];
  const invalid = getStringArray(body.invalid);
  const errors = Array.isArray(body.errors)
    ? body.errors
        .filter(
          (item): item is { email?: string; code: string; message: string } =>
            isRecord(item) && typeof item.code === 'string' && typeof item.message === 'string',
        )
        .map((item) => ({
          ...(typeof item.email === 'string' ? { email: item.email } : {}),
          code: item.code,
          message: item.message,
        }))
    : [];

  const emails = getStringArray(body.emails);
  const error = body.error;
  const generatedInvalidErrors = invalid.map((email) => ({
    email,
    code: emails.length > 0 ? 'invalid_invite_email' : getErrorCode(error),
    message: emails.length > 0 ? 'Enter a valid email address.' : getErrorMessage(error),
  }));
  const generatedEmailErrors = emails.map((email) => ({
    email,
    code: getErrorCode(error),
    message: getErrorMessage(error),
  }));

  if (errors.length > 0 || invitations.length > 0 || invalid.length > 0 || emails.length > 0) {
    return {
      invitations,
      invalid,
      errors: errors.length > 0 ? errors : [...generatedInvalidErrors, ...generatedEmailErrors],
    };
  }

  return null;
}

function normalizeInviteEmails(values: string[]): { emails: string[]; invalid: string[] } {
  const emails = new Set<string>();
  const invalid: string[] = [];

  for (const value of values) {
    const email = value.trim().toLowerCase();
    if (!email || !inviteEmailPattern.test(email)) {
      invalid.push(value);
      continue;
    }
    emails.add(email);
  }

  return { emails: Array.from(emails).sort(), invalid };
}

function localInviteId(email: string): string {
  return `local-invite-${email.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`;
}

function localObjectUrl(file: File, kind: 'avatar' | 'logo'): string {
  if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
    return URL.createObjectURL(file);
  }

  return `local-${kind}-${encodeURIComponent(file.name)}`;
}

export function normalizeWorkspaceSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export function getWorkspaceEntryPath(workspaceSlug: string): string {
  return `/${encodeURIComponent(workspaceSlug)}/feed`;
}

export function getWorkspaceRedirectLocation(
  workspaceSlug: string,
  redirect: string | null | undefined,
): WorkspaceRedirectLocation {
  const fallback = getWorkspaceEntryPath(workspaceSlug);
  if (!redirect) return { href: fallback, pathname: fallback, search: '' };

  const redirectPath = redirect.startsWith('/') ? redirect : `/${redirect}`;
  const pathname = redirectPath.split('?')[0] ?? '';
  const searchIndex = redirectPath.indexOf('?');
  const search = searchIndex >= 0 ? redirectPath.slice(searchIndex) : '';
  const [section] = pathname.split('/').filter(Boolean);
  if (!section || !legacyWorkspaceSections.has(section)) {
    return { href: fallback, pathname: fallback, search: '' };
  }

  // Home became Notifications, which became the Feed: an old link scopes to where the page lives now.
  const scopedSection = section === 'home' || section === 'notifications' ? '/feed' : pathname;
  const scopedPathname = `/${encodeURIComponent(workspaceSlug)}${scopedSection}`;
  return {
    href: `${scopedPathname}${search}`,
    pathname: scopedPathname,
    search,
  };
}

export function getWorkspaceRedirectPath(
  workspaceSlug: string,
  redirect: string | null | undefined,
): string {
  return getWorkspaceRedirectLocation(workspaceSlug, redirect).href;
}

export function getPostAuthDestination(workspaces: WorkspaceDestinationInput[]): string {
  if (workspaces.length === 0) return '/signup/workspace';
  if (workspaces.length > 1) return '/workspaces/select';
  const workspace = workspaces[0];
  if (!workspace) return '/signup/workspace';
  return workspace.onboardingComplete
    ? getWorkspaceEntryPath(workspace.slug)
    : `/${encodeURIComponent(workspace.slug)}/welcome`;
}

export async function resolvePostAuthDestination(
  loadWorkspaces: () => Promise<WorkspaceDestinationInput[]> = listWorkspaces,
): Promise<string> {
  return getPostAuthDestination(await loadWorkspaces());
}

export function getWorkspaceSlugFromPath(pathname: string): string | null {
  const [workspaceSlug, section] = pathname.split('/').filter(Boolean);
  if (!workspaceSlug || !section) return null;
  if (reservedTopLevelSections.has(workspaceSlug)) return null;
  if (!workspaceScopedSections.has(section)) return null;
  return decodeURIComponent(workspaceSlug);
}

export function isWorkspaceScopedPath(pathname: string): boolean {
  return getWorkspaceSlugFromPath(pathname) !== null;
}

export function getLegacyWorkspaceRedirectTarget(pathname: string, search = ''): string | null {
  const [section] = pathname.split('/').filter(Boolean);
  if (!section || !legacyWorkspaceSections.has(section)) return null;
  return `/workspaces/select?redirect=${encodeURIComponent(`${pathname}${search}`)}`;
}

export function getLegacyWorkspaceDestination(
  workspaces: WorkspaceDestinationInput[],
  pathname: string,
  search = '',
): string | null {
  const pickerTarget = getLegacyWorkspaceRedirectTarget(pathname, search);
  if (!pickerTarget) return null;
  if (workspaces.length === 0) return '/signup/workspace';
  if (workspaces.length > 1) return pickerTarget;

  const workspace = workspaces[0];
  if (!workspace) return '/signup/workspace';
  return workspace.onboardingComplete
    ? getWorkspaceRedirectPath(workspace.slug, `${pathname}${search}`)
    : `/${encodeURIComponent(workspace.slug)}/welcome`;
}

export function createPreviewWorkspaceSummary(input: CreateWorkspaceInput): WorkspaceSummary {
  return {
    id: `preview-${input.slug}`,
    name: input.name,
    slug: input.slug,
    role: 'owner',
    companyId: `preview-company-${input.slug}`,
    companyName: input.name,
    companyDomain: input.domainAccess ? (input.domains[0] ?? 'parcel.local') : null,
    companyDomains: input.domainAccess ? input.domains : [],
    logoUrl: null,
    onboardingComplete: false,
    settings: { fiscalYearStart: 'January' },
    capabilities: {
      canUpdateSettings: true,
      canManageMembers: true,
      canManageInvites: true,
      canUploadAssets: true,
      canManageApiKeys: true,
      canManageSpecTools: true,
      canManageConnections: true,
      canManageSkills: true,
      canUseDeveloperMcp: false,
    },
    effectivePlan: 'free',
    entitlements: PLAN_DEFAULTS.free,
  };
}

export async function listWorkspaces(): Promise<WorkspaceSummary[]> {
  if (!isSupabaseConfigured) return [];

  const data = await apiFetch<{ workspaces: WorkspaceSummary[] }>('/workspaces');
  return data.workspaces;
}

export async function getWorkspace(workspaceSlug: string): Promise<WorkspaceSummary> {
  if (!isSupabaseConfigured) {
    return createPreviewWorkspaceSummary({
      name: workspaceSlug,
      slug: workspaceSlug,
      domainAccess: false,
      domains: [],
    });
  }

  return apiFetch<WorkspaceSummary>(`/workspaces/${encodeURIComponent(workspaceSlug)}`);
}

export async function getUserProfile(): Promise<UserProfile> {
  if (!isSupabaseConfigured) {
    return {
      id: 'local-dev-user',
      email: 'local@parcel.dev',
      firstName: 'Local',
      lastName: 'Preview',
      displayName: null,
      title: null,
      avatarUrl: null,
    };
  }

  return apiFetch<UserProfile>('/me');
}

export async function getWorkspaceSecurityDomains(workspaceSlug: string): Promise<string[]> {
  if (!isSupabaseConfigured) return getLocalSecurityDomains(workspaceSlug);
  return (await getWorkspace(workspaceSlug)).companyDomains;
}

export async function addWorkspaceDomain(
  workspaceSlug: string,
  input: WorkspaceDomainInput,
): Promise<string[]> {
  if (!isSupabaseConfigured) {
    const domains = Array.from(
      new Set([...getLocalSecurityDomains(workspaceSlug), input.domain.toLowerCase()]),
    ).sort();
    localSecurityDomains.set(workspaceSlug, domains);
    return domains;
  }

  const data = await apiFetch<{ companyDomains: string[] }>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/domains`,
    {
      method: 'POST',
      body: JSON.stringify(input),
    },
  );
  return data.companyDomains;
}

export async function removeWorkspaceDomain(
  workspaceSlug: string,
  domain: string,
): Promise<string[]> {
  if (!isSupabaseConfigured) {
    const domains = getLocalSecurityDomains(workspaceSlug).filter((item) => item !== domain);
    localSecurityDomains.set(workspaceSlug, domains);
    return domains;
  }

  const data = await apiFetch<{ companyDomains: string[] }>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/domains/${encodeURIComponent(domain)}`,
    { method: 'DELETE' },
  );
  return data.companyDomains;
}

export async function createWorkspace(input: CreateWorkspaceInput): Promise<WorkspaceSummary> {
  if (!isSupabaseConfigured) return createPreviewWorkspaceSummary(input);

  return apiFetch<WorkspaceSummary>('/workspaces', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateWorkspace(
  workspaceSlug: string,
  input: UpdateWorkspaceInput,
  options?: RequestInit,
): Promise<WorkspaceSummary> {
  if (!isSupabaseConfigured) {
    const preview = createPreviewWorkspaceSummary({
      name: input.name ?? workspaceSlug,
      slug: workspaceSlug,
      domainAccess: false,
      domains: [],
    });
    return {
      ...preview,
      settings: {
        fiscalYearStart: input.settings?.fiscalYearStart ?? preview.settings.fiscalYearStart,
      },
    };
  }

  return apiFetch<WorkspaceSummary>(`/workspaces/${encodeURIComponent(workspaceSlug)}`, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export async function updateUserProfile(input: UserProfileUpdateInput): Promise<UserProfile> {
  const payload = normalizeUserProfileUpdateInput(input);

  if (!isSupabaseConfigured) {
    return {
      id: 'local-dev-user',
      email: 'local@parcel.dev',
      firstName: payload.firstName,
      lastName: payload.lastName,
      displayName: payload.displayName ?? null,
      title: payload.title ?? null,
      avatarUrl: null,
    };
  }

  return apiFetch<UserProfile>('/me', buildUserProfileUpdateRequest(input));
}

export async function listWorkspaceMembers(
  workspaceSlug: string,
): Promise<WorkspaceMembersResponse> {
  if (!isSupabaseConfigured) {
    return {
      canManageMembers: true,
      seats: { used: 1, limit: 999, isOver: false, quantity: 0, canManage: false },
      members: [
        {
          id: 'local-dev-user',
          type: 'member',
          name: 'Local Dev User',
          email: 'local@parcel.dev',
          role: 'owner',
          status: 'Active',
          lastActive: 'Never',
          avatarUrl: null,
        },
      ],
      invitations: [],
    };
  }

  return apiFetch<WorkspaceMembersResponse>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/members`,
  );
}

export async function inviteWorkspaceMembers(
  workspaceSlug: string,
  input: { emails: string[]; role: 'admin' | 'member' },
): Promise<InviteWorkspaceMembersResponse> {
  if (!isSupabaseConfigured) {
    const normalized = normalizeInviteEmails(input.emails);
    return {
      invitations: normalized.emails.map((email) => ({ id: localInviteId(email), email })),
      invalid: normalized.invalid,
      errors:
        normalized.invalid.length > 0
          ? normalized.invalid.map((email) => ({
              email,
              code: 'invalid_invite_email',
              message: 'Enter a valid email address.',
            }))
          : [],
    };
  }

  try {
    return await apiFetch<InviteWorkspaceMembersResponse>(
      `/workspaces/${encodeURIComponent(workspaceSlug)}/invitations`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    );
  } catch (err) {
    if (err instanceof ApiError) {
      const response = getInviteErrorResponse(err.body);
      if (response) return response;
    }
    throw err;
  }
}

export async function resendWorkspaceInvitation(
  workspaceSlug: string,
  invitationId: string,
): Promise<{ inviteUrl: string | null }> {
  if (!isSupabaseConfigured) return { inviteUrl: null };

  const response = await apiFetch<{ id: string; email: string; inviteUrl?: string }>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/invitations/${encodeURIComponent(
      invitationId,
    )}/resend`,
    {
      method: 'POST',
    },
  );
  return { inviteUrl: response.inviteUrl ?? null };
}

export async function revokeWorkspaceInvitation(
  workspaceSlug: string,
  invitationId: string,
): Promise<void> {
  if (!isSupabaseConfigured) return;

  await apiFetch<{ id: string; email: string }>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/invitations/${encodeURIComponent(
      invitationId,
    )}`,
    {
      method: 'DELETE',
    },
  );
}

export async function uploadUserAvatar(file: File): Promise<{ avatarUrl: string }> {
  if (!isSupabaseConfigured) return { avatarUrl: localObjectUrl(file, 'avatar') };

  const form = new FormData();
  form.set('file', file);
  return apiFetch<{ avatarUrl: string }>('/me/avatar', {
    method: 'POST',
    body: form,
    headers: {},
  });
}

export async function uploadWorkspaceLogo(
  workspaceSlug: string,
  file: File,
): Promise<{ logoUrl: string }> {
  if (!isSupabaseConfigured) return { logoUrl: localObjectUrl(file, 'logo') };

  const form = new FormData();
  form.set('file', file);
  return apiFetch<{ logoUrl: string }>(`/workspaces/${encodeURIComponent(workspaceSlug)}/logo`, {
    method: 'POST',
    body: form,
    headers: {},
  });
}

export async function completeWorkspaceOnboarding(
  workspaceSlug: string,
  input: { profileCompleted: boolean; invitesCompleted: boolean; completed: boolean },
): Promise<{ onboarding: OnboardingStatus }> {
  if (!isSupabaseConfigured) {
    return {
      onboarding: {
        profileCompleted: input.profileCompleted,
        invitesCompleted: input.invitesCompleted,
        completedAt: input.completed ? new Date().toISOString() : null,
      },
    };
  }

  return apiFetch(`/workspaces/${encodeURIComponent(workspaceSlug)}/onboarding`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}
